/**
 * O avaliador — de árvore para valor.
 *
 * ## Como o ciclo é detectado, e por que ANTES do `computed` (T2.3)
 *
 * O card diz: *"`A = B+1` e `B = A+1` com signals `computed` é laço infinito que trava a
 * aba"*. Está certo, e a consequência de desenho é mais forte do que parece.
 *
 * `@preact/signals-core` **não** protege contra ciclo entre `computed`s — ele detecta e lança
 * `Cycle detected`, e esse erro sobe pela árvore de render do Lit/React até derrubar a
 * visualização inteira, não só a célula. Ou seja: um usuário escrevendo uma fórmula circular
 * não perde a célula, perde a tabela.
 *
 * Então a detecção **não** pode acontecer dentro da avaliação reativa. Ela acontece:
 *
 *   1. no EDITOR, quando a fórmula é escrita — e aí a expressão é recusada com explicação;
 *   2. de novo AQUI, a cada avaliação, sobre o grafo de dependências — porque a fórmula de
 *      OUTRA coluna pode ter mudado depois, e o ciclo nasce de uma alteração que não passou
 *      por este editor.
 *
 * O segundo ponto é o que não se adivinha: a validação na escrita é necessária e **não é
 * suficiente**. Duas fórmulas individualmente válidas formam um ciclo quando a segunda é
 * editada, e quem edita a segunda vê o erro — mas a primeira também precisa parar de tentar.
 *
 * ⚠️ A travessia é feita sobre **propriedades**, não sobre células: o ciclo é uma propriedade
 * do schema (coluna A lê coluna B), não da linha. Percorrer por célula multiplicaria o custo
 * pelo número de linhas e responderia a mesma coisa.
 */

import { erro, ErroDeFormula } from './erros';
import type { No } from './ast';
import { propriedadesReferenciadas } from './ast';
import { chamar, type ContextoDeFuncoes } from './funcoes';
import {
  comoNumero, comparar, ehVazio, VAZIO, type Valor,
} from './valores';

/** O que o avaliador precisa do mundo — e nada além disso (D-D: puro, testável sem navegador). */
export interface ContextoDeAvaliacao extends ContextoDeFuncoes {
  /** O valor bruto de uma célula da MESMA linha. `undefined` = coluna inexistente. */
  valorDaCelula(propertyId: string): unknown;
  /**
   * A árvore da fórmula de outra coluna, quando ela também for de fórmula.
   * `null` para coluna comum. É o que permite enxergar o grafo inteiro.
   */
  formulaDaColuna(propertyId: string): No | null;
  /** O id da coluna que está sendo avaliada — a raiz da busca por ciclo. */
  propertyIdAtual: string;
}

/**
 * Há ciclo a partir desta coluna?
 *
 * Devolve o caminho quando há (`['A','B','A']`), para que a mensagem possa mostrá-lo — dizer
 * *"há um ciclo"* sem dizer **onde** deixa o usuário procurando entre todas as colunas.
 */
export function detectarCiclo(
  raiz: string,
  formulaDaColuna: (id: string) => No | null
): string[] | null {
  const emVisita = new Set<string>();
  const concluidas = new Set<string>();
  const caminho: string[] = [];

  const visitar = (id: string): string[] | null => {
    if (emVisita.has(id)) {
      const inicio = caminho.indexOf(id);
      return [...caminho.slice(inicio === -1 ? 0 : inicio), id];
    }
    if (concluidas.has(id)) return null;

    const arvore = formulaDaColuna(id);
    // Coluna comum não tem dependência — é folha, e é onde toda travessia termina.
    if (!arvore) { concluidas.add(id); return null; }

    emVisita.add(id);
    caminho.push(id);
    for (const dependencia of propriedadesReferenciadas(arvore)) {
      const achado = visitar(dependencia);
      if (achado) return achado;
    }
    caminho.pop();
    emVisita.delete(id);
    concluidas.add(id);
    return null;
  };

  return visitar(raiz);
}

/**
 * Avalia a árvore. Lança `ErroDeFormula` — nunca devolve valor de reserva.
 *
 * ⚠️ A detecção de ciclo roda ANTES de qualquer leitura de célula. Se rodasse depois, a
 * primeira leitura já teria disparado a recursão que se quer evitar.
 */
export function avaliar(arvore: No, ctx: ContextoDeAvaliacao): Valor {
  const ciclo = detectarCiclo(ctx.propertyIdAtual, ctx.formulaDaColuna);
  if (ciclo) {
    throw erro('ciclo',
      `esta fórmula depende de si mesma (${ciclo.length - 1} passo(s) no caminho). `
      + 'Uma coluna de fórmula não pode se referenciar, nem direta nem indiretamente');
  }
  return avaliarNo(arvore, ctx);
}

function avaliarNo(no: No, ctx: ContextoDeAvaliacao): Valor {
  switch (no.tipo) {
    case 'numero': return no.valor;
    case 'texto': return no.valor;
    case 'booleano': return no.valor;

    case 'prop': {
      // ⚠️ **Coluna de fórmula é lida AVALIANDO, não lendo a célula.**
      //
      // Isto não estava no card e apareceu no primeiro teste do diamante (A lê B e C, ambos
      // leem D). O motivo é estrutural: uma coluna de fórmula é DERIVADA — o `rawValue.setValue`
      // dela é um no-op, exatamente como o `created-by` do upstream, e por isso ela **não tem
      // valor guardado em `cells`**. `dataSource.cellValueGet` sobre ela devolve o default, que
      // é vazio.
      //
      // Sem este ramo, `prop("Saldo final")` numa segunda fórmula devolveria vazio — e vazio
      // se propaga (ver `valores.ts`), então a coluna inteira ficaria em branco **sem erro
      // nenhum**. É o formato de defeito que este repositório mais cataloga, e ele estaria
      // exatamente no caso mais comum de planilha financeira: `margem = resultado / receita`,
      // onde `resultado` já é fórmula.
      //
      // A recursão é segura porque `avaliar()` já provou a ausência de ciclo ANTES de chegar
      // aqui — e é justamente a existência deste ramo que torna o ciclo possível, e a T2.3
      // necessária. Uma implementação sem ele não teria o que detectar.
      const formulaDependente = ctx.formulaDaColuna(no.propertyId);
      if (formulaDependente) {
        return avaliarNo(formulaDependente, { ...ctx, propertyIdAtual: no.propertyId });
      }

      const bruto = ctx.valorDaCelula(no.propertyId);
      if (bruto === undefined) {
        // A coluna sumiu DEPOIS que a fórmula foi escrita — apagada por alguém. É um erro
        // próprio (T2.4) porque o conserto é diferente do de sintaxe: a pessoa precisa
        // reescrever a referência, não corrigir uma digitação.
        throw erro('coluna-ausente',
          `a coluna "${no.nomeOriginal}" não existe mais — a fórmula precisa ser ajustada`);
      }
      return normalizarValorDeCelula(bruto);
    }

    case 'unario': {
      const v = avaliarNo(no.operando, ctx);
      if (no.operador === '-') {
        return ehVazio(v) ? VAZIO : -comoNumero(v, 'negativo');
      }
      return chamar('not', [v], ctx);
    }

    case 'binario': {
      const a = avaliarNo(no.esquerda, ctx);
      const b = avaliarNo(no.direita, ctx);
      return aplicarBinario(no.operador, a, b);
    }

    case 'chamada': {
      const argumentos = no.argumentos.map(arg => avaliarNo(arg, ctx));
      try {
        return chamar(no.nome, argumentos, ctx);
      } catch (e) {
        if (e instanceof ErroDeFormula && e.posicao === null) {
          throw erro(e.codigo, e.message, no.posicao);
        }
        throw e;
      }
    }

    default:
      throw erro('sintaxe', 'nó de árvore desconhecido');
  }
}

function aplicarBinario(operador: string, a: Valor, b: Valor): Valor {
  if (operador === '==' || operador === '!=' || operador === '>' || operador === '<'
    || operador === '>=' || operador === '<=') {
    return comparar(operador, a, b);
  }

  // Aritmética propaga vazio (ver a nota longa em `valores.ts`).
  if (ehVazio(a) || ehVazio(b)) return VAZIO;

  const x = comoNumero(a, `operador "${operador}"`);
  const y = comoNumero(b, `operador "${operador}"`);

  switch (operador) {
    case '+': return x + y;
    case '-': return x - y;
    case '*': return x * y;
    case '/':
      if (y === 0) {
        // ⚠️ Devolver `Infinity` seria pior que lançar: ele soma, formata como "∞" e
        // atravessa a coluna inteira. Um erro visível é o único desfecho que o usuário
        // consegue consertar.
        throw erro('divisao-por-zero', 'divisão por zero');
      }
      return x / y;
    default:
      throw erro('sintaxe', `operador desconhecido: "${operador}"`);
  }
}

/** Reexportado para que quem só precisa converter não importe `valores.js` inteiro. */
import { normalizar as normalizarValorDeCelula } from './valores';
export { normalizarValorDeCelula };
